import React from 'react'
import clsx from 'clsx'
import { Box, Divider, Typography, IconButton, Menu, MenuItem, Badge, Popover, Avatar, Dialog, DialogContent, DialogActions, Button } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { makeStyles } from '@material-ui/core/styles'
import NotificationsOutlinedIcon from '@material-ui/icons/NotificationsOutlined'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import SettingsOutlinedIcon from '@material-ui/icons/SettingsOutlined'
import PersonOutlineOutlinedIcon from '@material-ui/icons/PersonOutlineOutlined'
import LogoutIcon from 'assets/images/svgs/logout.svg'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation, useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { toggleMobileMenu } from 'reducers/stateReducer'
import MenuIcon from '@material-ui/icons/Menu'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { IRootReduxState, IUserState } from 'types/reducers'
import UserAvatar from 'components/UserAvatar'
import userManager from 'utils/userManager'
import { PUBLIC_SANS_FONT_FAMILY, SIDEBAR_MENU_FONT_WEIGHT } from 'etc/appFont'
import { lookupRouteHeaderTitle } from 'etc/routeHeaderTitles'
import NotificationBell from '../NotificationBell'

export interface TopHeaderDynamicProps {
  /**
   * Explicit title. Omit to use global title: Redux `pageTitle` (from `setPageTitle`), then route fallback.
   */
  title?: React.ReactNode
  component?: React.ElementType
  className?: string
}

const useStyles = makeStyles((theme) => ({
  bar: {
    width: '100%',
    boxSizing: 'border-box',
    background: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: '0 24px',
    height: 80,
    display: 'flex',
    alignItems: 'center',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(1),
  },
  title: {
    flex: '1 1 auto',
    minWidth: 0,
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontSize: 24,
    fontWeight: 600,
    fontStyle: 'normal',
    lineHeight: '38.4px',
    letterSpacing: 0,
    color: theme.palette.text.primary,
    verticalAlign: 'middle',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing(1.5),
  },
  iconButton: {
    padding: 8,
    color: theme.palette.text.secondary,
  },
  notificationDot: {
    backgroundColor: '#E11D48',
    minWidth: 8,
    height: 8,
    borderRadius: '50%',
    border: `1.5px solid ${theme.palette.background.paper}`,
    top: 2,
    right: 2,
  },
  profileIconButton: {
    padding: '4px 6px 4px 4px',
  },
  profileTrigger: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  profileName: {
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
    textTransform: 'none',
    whiteSpace: 'nowrap',
    maxWidth: 160,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  profileChevron: {
    fontSize: 20,
    color: theme.palette.text.secondary,
    transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
  },
  profileChevronOpen: {
    transform: 'rotate(180deg)',
  },
  actionsDivider: {
    width: 1,
    height: 24,
    flexShrink: 0,
    alignSelf: 'center',
    marginLeft: theme.spacing(0.75),
    marginRight: theme.spacing(0.75),
    backgroundColor: theme.palette.divider,
  },
  menuPaper: {
    minWidth: 196,
    background: theme.palette.background.paper,
    borderRadius: 4,
    boxShadow: theme.shadows[8],
    border: `1px solid ${theme.palette.divider}`,
  },
  /** Inset from menu edges like Figma (not full-bleed) */
  menuDivider: {
    backgroundColor: theme.palette.divider,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  menuItem: {
    padding: '12px 16px',
    color: theme.palette.text.primary,
    fontSize: 14,
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontWeight: 400,
    letterSpacing:'-0.31px',
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  /** Static user summary — paper bg; no hover highlight */
  menuItemUser: {
    cursor: 'default',
    paddingTop: 16,
    paddingBottom: 16,
    alignItems: 'flex-start',
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
    '&:focus': {
      backgroundColor: theme.palette.background.paper,
    },
    '&.Mui-focusVisible': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  menuUserRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    minWidth: 0,
  },
  menuUserAvatar: {
    flexShrink: 0,
  },
  menuUserText: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 2,
    minWidth: 0,
    flex: 1,
  },
  menuUserName: {
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.35,
    color: theme.palette.text.primary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  menuUserUsername: {
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  menuItemInner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    width: '100%',
  },
  /** Fixed column so labels align with Figma */
  menuItemIconSlot: {
    flexShrink: 0,
    width: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  menuItemIcon: {
    fontSize: 20,
    color: theme.palette.text.primary,
  },
  menuItemLogout: {
    color: theme.palette.type === 'dark' ? '#f87171' : '#d32f2f',
    '&:hover': {
      backgroundColor: 'rgba(234, 35, 35, 0.26)',
    },
    '&:focus': {
      backgroundColor: 'rgba(234, 35, 35, 0.26)',
    },
  },
  /** Original logout.svg is fill=white; tint to brand red — keep white in dark mode for contrast */
  menuLogoutSvg: {
    width: 20,
    height: 20,
    display: 'block',
    filter: theme.palette.type === 'dark'
      ? 'brightness(0) saturate(100%) invert(72%) sepia(57%) saturate(4583%) hue-rotate(327deg) brightness(101%) contrast(95%)'
      : 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)',
  },
  // ── Sign-out confirmation dialog ──────────────────────────────────────────
  signOutDialog: {
    '& .MuiDialog-paper': {
      borderRadius: 16,
      padding: '32px 24px 24px',
      maxWidth: 400,
      width: '100%',
      textAlign: 'center',
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
  },
  signOutIconWrap: {
    width: 72,
    height: 72,
    borderRadius: '50%',
    background: 'rgba(239,68,68,0.10)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  signOutCloseBtn: {
    position: 'absolute' as const,
    top: 12,
    right: 12,
    color: theme.palette.text.secondary,
    '&:hover': { color: theme.palette.text.primary },
  },
  signOutTitle: {
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 18,
    color: theme.palette.text.primary,
    marginBottom: 10,
  },
  signOutBody: {
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontSize: 14,
    color: theme.palette.text.secondary,
    lineHeight: 1.55,
    marginBottom: 28,
  },
  signOutActions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
    padding: 0,
  },
  signOutCancelBtn: {
    flex: 1,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: '12px 0',
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontWeight: 600,
    fontSize: 14,
    color: theme.palette.text.primary,
    textTransform: 'none' as const,
    background: theme.palette.background.paper,
    '&:hover': { background: theme.palette.action.hover },
  },
  signOutConfirmBtn: {
    flex: 1,
    borderRadius: 8,
    padding: '12px 0',
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontWeight: 700,
    fontSize: 14,
    color: '#fff',
    textTransform: 'none' as const,
    background: '#163A64',
    boxShadow: 'none',
    '&:hover': { background: '#0f2a4a', boxShadow: 'none' },
  },
  // ──────────────────────────────────────────────────────────────────────────
  langMenuPaper: {
    minWidth: 160,
    background: theme.palette.background.paper,
    borderRadius: 4,
    boxShadow: theme.shadows[8],
    border: `1px solid ${theme.palette.divider}`,
  },
  langMenuItem: {
    padding: '10px 16px',
    fontSize: 14,
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontWeight: 400,
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    '&:hover': {
      background: theme.palette.action.hover,
    },
  },
  langMenuItemActive: {
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#60A5FA' : '#155DFC',
  },
  langCheckIcon: {
    fontSize: 16,
    color: theme.palette.type === 'dark' ? '#60A5FA' : '#155DFC',
    flexShrink: 0,
  },
  notifPaper: {
    width: 360,
    borderRadius: 8,
    boxShadow: theme.shadows[8],
    border: '1px solid rgba(0,0,0,0.08)',
    overflow: 'hidden',
  },
  notifHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 16px 10px',
    borderBottom: '1px solid #E4E7EC',
  },
  notifTitle: {
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontWeight: 600,
    fontSize: 15,
    color: '#101828',
  },
  notifBadgeCount: {
    background: '#E11D48',
    color: '#fff',
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 8,
    padding: '1px 7px',
  },
  notifList: {
    maxHeight: 380,
    overflowY: 'auto' as const,
  },
  notifItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 16px',
    borderBottom: '1px solid #F2F4F7',
    cursor: 'default',
    '&:last-child': { borderBottom: 'none' },
  },
  notifItemUnread: {
    background: '#F9FAFB',
  },
  notifAvatar: {
    width: 36,
    height: 36,
    flexShrink: 0,
    fontSize: 14,
  },
  notifContent: {
    flex: 1,
    minWidth: 0,
  },
  notifMessage: {
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontSize: 13,
    color: '#344054',
    lineHeight: 1.45,
  },
  notifTime: {
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontSize: 11,
    color: '#98A2B3',
    marginTop: 3,
  },
  notifUnreadDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    background: '#E11D48',
    flexShrink: 0,
    marginTop: 6,
  },
  notifFooter: {
    padding: '10px 16px',
    borderTop: '1px solid #E4E7EC',
    textAlign: 'center' as const,
    fontFamily: PUBLIC_SANS_FONT_FAMILY,
    fontSize: 13,
    color: '#155DFC',
    cursor: 'pointer',
    '&:hover': { background: '#F5F8FF' },
  },
}))

const logout = async () => {
  localStorage.removeItem('workaware-persisted-user')
  localStorage.removeItem('workaware-remember-me')
  await userManager.removeUser()
  window.location.replace('/')
}

/** Readable fallback from the last URL segment (kebab-case, camelCase, simple acronyms). */
function humanizePathSegment(segment: string): string {
  if (!segment) return ''
  let s = segment.replace(/-/g, ' ')
  s = s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/([0-9])([A-Za-z])/g, '$1 $2')
    .replace(/([A-Za-z])([0-9])/g, '$1 $2')
  return s
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function useResolvedTitle(explicit?: React.ReactNode): React.ReactNode {
  const location = useLocation()
  const pageTitleFromStore = useSelector((state: IRootReduxState) => state.app.state.pageTitle)
  const pageTitlePath = useSelector((state: IRootReduxState) => state.app.state.pageTitlePath)

  return React.useMemo(() => {
    if (explicit !== undefined) {
      return explicit
    }
    const path = location.pathname
    const storeTitleApplies =
      pageTitleFromStore !== '' &&
      pageTitleFromStore != null &&
      pageTitlePath != null &&
      pageTitlePath === path

    if (storeTitleApplies) {
      return pageTitleFromStore as React.ReactNode
    }
    const routeTitle = lookupRouteHeaderTitle(path)
    if (routeTitle) {
      return (
        <FormattedMessage id={routeTitle.id} defaultMessage={routeTitle.defaultMessage} />
      )
    }
    if (path === '/' || path === '') {
      return <FormattedMessage id="menu.main.dashboard" defaultMessage="Dashboard" />
    }
    const last = path.split('/').filter(Boolean).pop() || ''
    const human = humanizePathSegment(last)
    return human || 'Workaware'
  }, [explicit, pageTitleFromStore, pageTitlePath, location.pathname])
}

/**
 * Page header: title (left), notifications + profile menu (right) — profile menu matches sidebar profile actions.
 */
const TopHeaderDynamic: React.FC<TopHeaderDynamicProps> = ({ title: titleProp, component = 'h2', className }) => {
  const classes = useStyles()
  const intl = useIntl()
  const history = useHistory()
  const title = useResolvedTitle(titleProp)
  const user = useSelector<IRootReduxState, IUserState>((state) => state.app.user).current
  const [profileAnchor, setProfileAnchor] = React.useState<null | HTMLElement>(null)
  const profileMenuOpen = Boolean(profileAnchor)
  const [confirmLogoutOpen, setConfirmLogoutOpen] = React.useState(false)

  const openProfileMenu = (e: React.MouseEvent<HTMLElement>) => {
    setProfileAnchor(e.currentTarget)
  }

  const closeProfileMenu = () => {
    setProfileAnchor(null)
  }

  const goProfile = () => {
    closeProfileMenu()
    history.push('/profile')
  }

  const goSettings = () => {
    closeProfileMenu()
    // history.push('/setting/globalsettings')
  }

  const displayName = user ? [user.Lastname, user.Firstname].filter(Boolean).join(' ') || user.Username || '' : ''
  const dispatch = useDispatch()
  const smallScreen = useMediaQuery('(max-width:959px)')

  return (
    <Box
      className={classes.bar}
      onClick={(e) => {
        // Ignore clicks on top header background/title area so it does not route away.
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <Box className={classes.row}>
        {smallScreen && user && (
          <IconButton
            aria-label={intl.formatMessage({ id: 'navigation.openDrawer', defaultMessage: 'Open menu' })}
            onClick={(e) => { e.stopPropagation(); dispatch(toggleMobileMenu()) }}
            size="small"
            style={{ marginRight: 8 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Typography
          component={component}
          className={clsx(classes.title, className)}
          noWrap
          onClick={(e) => {
            // Prevent accidental top-bar clicks from triggering app-level routing behaviors.
            e.preventDefault()
            e.stopPropagation()
          }}
          style={{ cursor: 'default' }}
        >
          {title}
        </Typography>
        {user && (
          <Box className={classes.actions}>
            <NotificationBell />
            <Box className={classes.actionsDivider} component="span" aria-hidden />
            <IconButton
              className={classes.profileIconButton}
              aria-label={intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' })}
              aria-controls="top-header-profile-menu"
              aria-haspopup="true"
              aria-expanded={profileMenuOpen}
              onClick={openProfileMenu}
              edge="end"
            >
              <span className={classes.profileTrigger}>
                <UserAvatar swapColours={false} size={32} username={user.Username} photoUrl={user.PhotoURL} />
                {displayName ? <span className={classes.profileName}>{displayName}</span> : null}
                <KeyboardArrowDownIcon
                  className={clsx(classes.profileChevron, profileMenuOpen && classes.profileChevronOpen)}
                  aria-hidden
                />
              </span>
            </IconButton>
            <Menu
              id="top-header-profile-menu"
              anchorEl={profileAnchor}
              open={profileMenuOpen}
              onClose={closeProfileMenu}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{ disablePadding: true, autoFocusItem: false }}
              PaperProps={{ className: classes.menuPaper }}
            >
              <MenuItem
                className={clsx(classes.menuItem, classes.menuItemUser)}
                component="div"
                disableRipple
                tabIndex={-1}
              >
                <div className={classes.menuUserRow}>
                  <span className={classes.menuUserAvatar}>
                    <UserAvatar swapColours={false} size={40} username={user.Username} photoUrl={user.PhotoURL} />
                  </span>
                  <div className={classes.menuUserText}>
                    <div className={classes.menuUserName}>
                      {[user.Lastname, user.Firstname].filter(Boolean).join(' ') || user.Username || '—'}
                    </div>
                    {user.Username ? <div className={classes.menuUserUsername}>{user.Username}</div> : null}
                  </div>
                </div>
              </MenuItem>
              {/* Profile menu item hidden */}
              {/* <Divider className={classes.menuDivider} /> */}
              {/* <MenuItem className={classes.menuItem} onClick={goProfile}>
                <span className={classes.menuItemInner}>
                  <span className={classes.menuItemIconSlot}>
                    <PersonOutlineOutlinedIcon className={classes.menuItemIcon} aria-hidden />
                  </span>
                  {intl.formatMessage({ id: 'profile', defaultMessage: 'Profile' })}
                </span>
              </MenuItem> */}
              {/* Settings menu item hidden */}
              {/* <MenuItem className={classes.menuItem} onClick={goSettings}>
                <span className={classes.menuItemInner}>
                  <span className={classes.menuItemIconSlot}>
                    <SettingsOutlinedIcon className={classes.menuItemIcon} aria-hidden />
                  </span>
                  {intl.formatMessage({ id: 'menu.main.settings.header', defaultMessage: 'Settings' })}
                </span>
              </MenuItem> */}
              <Divider className={classes.menuDivider} />
              <MenuItem
                className={clsx(classes.menuItem, classes.menuItemLogout)}
                onClick={() => {
                  closeProfileMenu()
                  setConfirmLogoutOpen(true)
                }}
              >
                <span className={classes.menuItemInner}>
                  <span className={classes.menuItemIconSlot}>
                    <img src={LogoutIcon} alt="" className={classes.menuLogoutSvg} width={20} height={20} />
                  </span>
                  {intl.formatMessage({ id: 'signout.menu', defaultMessage: 'Sign Out' })}
                </span>
              </MenuItem>
            </Menu>

            {/* Sign-out confirmation dialog */}
            <Dialog
              open={confirmLogoutOpen}
              onClose={() => setConfirmLogoutOpen(false)}
              className={classes.signOutDialog}
              maxWidth="xs"
              fullWidth
            >
              <IconButton
                size="small"
                className={classes.signOutCloseBtn}
                onClick={() => setConfirmLogoutOpen(false)}
                aria-label={intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              <DialogContent style={{ padding: 0 }}>
                <Box className={classes.signOutIconWrap}>
                  <img src={LogoutIcon} alt="" style={{ width: 32, height: 32, filter: 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(346deg) brightness(104%) contrast(97%)' }} />
                </Box>
                <Typography className={classes.signOutTitle}>
                  {intl.formatMessage({ id: 'signout.title', defaultMessage: 'Sign out of your account?' })}
                </Typography>
                <Typography className={classes.signOutBody}>
                  {intl.formatMessage({
                    id: 'signout.body',
                    defaultMessage: "You'll need to sign in again to access your workspace and any unsaved changes will be lost.",
                  })}
                </Typography>
              </DialogContent>

              <DialogActions className={classes.signOutActions} disableSpacing>
                <Button
                  className={classes.signOutCancelBtn}
                  onClick={() => setConfirmLogoutOpen(false)}
                  disableElevation
                >
                  {intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
                </Button>
                <Button
                  className={classes.signOutConfirmBtn}
                  onClick={() => {
                    setConfirmLogoutOpen(false)
                    void logout().catch((err) => console.error('Logout failed:', err))
                  }}
                  startIcon={<img src={LogoutIcon} alt="" style={{ width: 18, height: 18, filter: 'brightness(0) invert(1)' }} />}
                  disableElevation
                  variant="contained"
                >
                  {intl.formatMessage({ id: 'signout.confirm', defaultMessage: 'Sign out' })}
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default TopHeaderDynamic
